<template>
	<ClientOnly>
		<CardGeneric
			v-if="shouldShowInstallments"
			class="text-left text-primary checkoutInstallmentsComponent">
			<p class="cardTitle md:hidden">{{ $t("paymentInstalments") }}</p>

			<div
				class="flex justify-between gap-2"
				:class="{ 'mb-4': numInstallments }">
				<div>
					<p class="cardTitle hidden-mobile-block">{{ $t("paymentInstalments") }}</p>
					<p class="cardSubTitle text-secondary mb-0">{{ $t("paymentInstalmentsSubtitle") }}</p>
				</div>

				<FormDropdown
					id="numInstalments"
					class="justify-center my-auto w-24"
					:key="maxInstallmentCount + numInstallments"
					:disabled="!numInstallments || !hasInstallmentEligibleItems"
					:disable-search="true"
					:options="installmentOptions"
					:initial-value="numInstallments"
					@changed="handleFieldChange" />
			</div>

			<div v-if="nonEligibleItems.length > 0 && installments.length > 1 && total > 0">
				<p class="text-ternary text-xs">{{ $t("installmentsNote") }}</p>

				<ul class="list-disc mt-1 pl-5 text-primary font-serif">
					<li v-if="isRefundableBookingSelected">
						Refundable Booking ({{ currency.symbol }}{{ refundableBookingCost }})
					</li>
					<li v-for="item in nonEligibleItems">
						({{ item.quantity }}) {{ item.title }} ({{ currency.symbol }}{{ item.price }})
					</li>
				</ul>
			</div>

			<CardHighlightRow
				v-if="hasInstallmentEligibleItems"
				v-for="installment in installments"
				:key="installment.date + installment.amount"
				bg-color="bg-secondary"
				class="my-2">
				<p>{{ $t("paymentDue", { date: installment.date }) }}</p>
				<p class="font-serif">{{ currency.symbol }}{{ parseFloat(installment.amount).toFixed(2) }}</p>
			</CardHighlightRow>

			<CardSecondary
				v-if="!hasInstallmentEligibleItems"
				class="!p-2 !rounded-md mt-4 nonEligible">
				<p class="font-thin text-sm">{{ $t("installmentsNotEligible") }}</p>
			</CardSecondary>
		</CardGeneric>
	</ClientOnly>
</template>

<script setup>
	import { useNuxtApp } from "#app"
	import { useCurrencyStore } from "~/stores/CurrencyStore"
	import { storeToRefs } from "pinia"
	import { useCartStore } from "~/stores/CartStore"
	import { useSettingsStore } from "~/stores/SettingsStore"

	const { $cartService } = useNuxtApp()
	const runtimeConfig = useRuntimeConfig()

	const cartStore = useCartStore()
	const { items, installments, total, isRefundableBookingSelected, refundableBookingCost } = storeToRefs(cartStore)

	const currencyStore = useCurrencyStore()
	const { currency } = storeToRefs(currencyStore)

	const settingsStore = useSettingsStore()
	const { maxInstallmentCount } = storeToRefs(settingsStore)
	const shouldShowInstallments = maxInstallmentCount.value > 1

	const installmentOptions = computed(() => {
		let options = []
		for (let i = maxInstallmentCount.value; i > 0; i--) {
			options.push({ value: i, label: i })
		}
		return options
	})

	const numInstallments = computed(() => (installments.value ? installments.value.length : 1))

	const handleFieldChange = ({ value }) => {
		$cartService.$updateInstallments(value)
	}

	const nonEligibleItems = computed(() => {
		let nonEligibleItems = []
		items.value.forEach((item) => {
			if (!item.isInstallmentEligible) {
				nonEligibleItems.push({
					quantity: item.quantity,
					title: item.title,
					price: item.price,
				})
			}
		})
		return nonEligibleItems
	})

	const hasInstallmentEligibleItems = computed(() => {
		const allCartItems = [...cartStore.items.values()]
		return allCartItems.some((item) => item.isInstallmentEligible)
	})

	//instalmentsDropdownOffset is used for offsetting the dropdown properly on a small screen
	const instalmentsDropdownOffset = computed(() => {
		let multiplier = maxInstallmentCount.value < 5 ? maxInstallmentCount.value : 5
		return `-${multiplier * 2.45 + 2.5}em`
	})
</script>

<!--Note that the style is not scoped here, so we can style the child component-->
<style lang="scss">
	#numInstalments-options {
		@apply top-12;
		@apply max-lg:top-[v-bind(instalmentsDropdownOffset)];
	}
</style>
